import {
  CloudflareProvider,
  type DnsRequirement,
  ResendProvider,
  SesProvider,
  SmtpProvider,
} from '@mailysend/providers'
import { Input, useCopy } from '@mailysend/ui'
import { useId, useState } from 'react'
import { WidgetField, WidgetShell } from './widget-shell.tsx'

/**
 * Your DNS records, from the adapters that would verify them.
 *
 * Each adapter's `dnsRecords()` is a pure function of the domain, the selector
 * and its own configuration — no network, no credentials used — so calling it
 * here produces exactly the rows the setup screen would show you. A guide that
 * typed the records out by hand is how you end up publishing a `cf-bounce`
 * CNAME where Cloudflare uses MX, which this repo has already done once.
 *
 * The credentials below are placeholders and are never used: `dnsRecords()`
 * reads only the region (SES) and the host (SMTP) out of its config.
 */
const unusedConnect = () => {
  throw new Error('The DNS builder never opens a connection.')
}

const providers = {
  cloudflare: new CloudflareProvider({ accountId: '', apiToken: '' }),
  ses: new SesProvider({ accessKeyId: '', secretAccessKey: '', region: 'us-east-1' }),
  resend: new ResendProvider({ apiKey: '' }),
  smtp: new SmtpProvider({
    host: 'smtp.your-relay.com',
    port: 587,
    secure: 'starttls',
    connect: unusedConnect,
  }),
}

const LABELS: Array<[keyof typeof providers, string]> = [
  ['cloudflare', 'Cloudflare Email Service'],
  ['ses', 'Amazon SES'],
  ['resend', 'Resend'],
  ['smtp', 'SMTP relay'],
]

/** One hook per button: `useCopy`'s `copied` is a boolean, so sharing one
    instance across the rows would light up every button at once. */
const CopyButton = ({ value }: { value: string }) => {
  const { copied, copy } = useCopy()
  return (
    <button
      type="button"
      onClick={() => copy(value)}
      className="shrink-0 rounded-chip border border-line bg-card px-2 py-0.5 text-[11.5px] text-muted hover:border-ink hover:text-ink"
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

const RecordRows = ({ records }: { records: DnsRequirement[] }) => (
  <div className="flex flex-col gap-2.5">
    {records.map((record) => (
      <div
        key={`${record.record}-${record.name}-${record.value}`}
        className="rounded-tile border border-line bg-tint p-3.5"
      >
        <div className="flex flex-wrap items-baseline gap-2.5">
          <span className="font-mono text-[11.5px] font-bold text-accent">{record.record}</span>
          <span className="font-mono text-[13px] break-all text-ink">{record.name}</span>
          {record.priority !== undefined ? (
            <span className="font-mono text-[11.5px] text-muted-2">priority {record.priority}</span>
          ) : null}
          {record.origin === 'observe' ? (
            <span className="rounded-chip bg-card px-2 py-0.5 font-mono text-[10.5px] text-muted-2">
              published for you
            </span>
          ) : null}
        </div>
        <div className="mt-1.5 flex items-start gap-2">
          <code className="min-w-0 flex-1 font-mono text-[12.5px] break-all text-muted">
            {record.value}
          </code>
          <CopyButton value={record.value} />
        </div>
        <p className="mt-1.5 mb-0 text-[13px] leading-[1.5] text-muted-2">{record.purpose}</p>
      </div>
    ))}
  </div>
)

export const DnsRecordBuilder = () => {
  const id = useId()
  const [domain, setDomain] = useState('yourdomain.com')
  const [transport, setTransport] = useState<keyof typeof providers>('cloudflare')

  // A sanitised domain, so a half-typed value cannot produce a record that
  // looks publishable. Never a network call — this is pure string work.
  const safeDomain = domain
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '')
  const records = providers[transport].dnsRecords(safeDomain || 'yourdomain.com', {
    selector: 'ms1',
    returnPath: 'bounce',
    dkimPublicKey: 'MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA…',
  })

  return (
    <WidgetShell
      title="DNS RECORDS"
      source={
        <>
          Generated by each adapter's own <code className="font-mono">dnsRecords()</code> in{' '}
          <code className="font-mono">@mailysend/providers</code> — the same function that produces
          the rows on your setup screen.
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <WidgetField label="Your sending domain" htmlFor={`${id}-domain`}>
            <Input
              id={`${id}-domain`}
              value={domain}
              spellCheck={false}
              onChange={(event) => setDomain(event.target.value)}
              className="font-mono text-[13px]"
            />
          </WidgetField>
          <WidgetField label="Transport" htmlFor={`${id}-transport`}>
            <div className="flex flex-wrap gap-2" id={`${id}-transport`}>
              {LABELS.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={transport === key}
                  onClick={() => setTransport(key)}
                  className={
                    transport === key
                      ? 'rounded-chip border border-ink bg-ink px-3 py-1.5 text-[12.5px] text-paper'
                      : 'rounded-chip border border-line bg-tint px-3 py-1.5 text-[12.5px] text-muted hover:border-ink hover:text-ink'
                  }
                >
                  {label}
                </button>
              ))}
            </div>
          </WidgetField>
        </div>

        <RecordRows records={records} />

        <p className="m-0 text-[13px] leading-[1.55] text-muted-2">
          Rows marked <em>published for you</em> are written by the provider when you onboard the
          domain — there is nothing to copy, and verification confirms they exist rather than
          blocking on you. The DKIM value shown is a placeholder wherever the provider mints its own
          key under its own selector: printing a key that will not be the real one is worse than
          printing none.
        </p>
      </div>
    </WidgetShell>
  )
}
